import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Type, Divider } from "discover.medical.react-component-library";
import { PageTitle } from "../../components";
import { RegimenList, FilterControls } from "../../components/ChemoRegimens";
import {
  StyledWrapper,
  StyledFiltersWrapper,
  StyledSearchBar,
  StyledListWrapper,
  StyledNavButton,
} from "./_.styled";
import { transformRegimens } from "./transformUtils";
import {
  useRegimenSearch,
  useRegimenFilter,
  useRegimenSort,
  useTelemetry,
} from "./hooks";

const BACK_ICON_GLYPH = "navigate_before";

const ChemoRegimensFilter = ({
  backButton,
  title,
  subTitle,
  sort,
  searchBarLabel,
  regimens,
  filter,
  itemLabels,
  noDataText,
  sharedTelemetryData,
}) => {
  const filterType = filter.type;

  const { onRegimenClick, logSortUpdated, logFilterUpdated } = useTelemetry({
    sharedTelemetryData,
  });
  const {
    searchTerm,
    handleSearchTermChange,
    searchUtils,
  } = useRegimenSearch();
  const { selectedSortOption, setSelectedSortOption } = useRegimenSort({
    options: sort.options,
    logSortUpdated,
  });
  const {
    selectedFilterOption,
    setSelectedFilterOption,
    filterOptions,
  } = useRegimenFilter({ regimens, filterType, logFilterUpdated });

  const transformedRegimens = useMemo(
    () =>
      transformRegimens({
        regimens,
        filterType,
        filterOption: selectedFilterOption,
        sortOption: selectedSortOption,
        searchUtils,
      }),
    [
      regimens,
      selectedFilterOption,
      selectedSortOption,
      searchUtils,
      filterType,
    ]
  );

  return (
    <StyledWrapper>
      <StyledNavButton
        label={backButton.label}
        href={backButton.href}
        glyph={BACK_ICON_GLYPH}
        floatLeft
      />
      <Type element="p" fontSize="sm" textTransform="uppercase">
        {subTitle}
      </Type>
      <PageTitle>{title}</PageTitle>
      <StyledFiltersWrapper>
        <FilterControls
          filterPlaceholder={filter.placeholder}
          filterOptions={filterOptions}
          selectedFilterOption={selectedFilterOption}
          onFilterChange={setSelectedFilterOption}
          sortLabel={sort.label}
          sortOptions={sort.options}
          selectedSortOption={selectedSortOption}
          onSortOptionChange={setSelectedSortOption}
        />
      </StyledFiltersWrapper>
      <Divider />
      <StyledSearchBar
        value={searchTerm}
        placeholder={searchBarLabel}
        onChange={handleSearchTermChange}
      />
      <StyledListWrapper>
        <RegimenList
          regimens={transformedRegimens}
          onRegimenClick={onRegimenClick}
          itemLabels={itemLabels}
          noDataText={noDataText}
        />
      </StyledListWrapper>
    </StyledWrapper>
  );
};

ChemoRegimensFilter.propTypes = {
  backButton: PropTypes.shape({
    href: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  sort: PropTypes.shape({
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  searchBarLabel: PropTypes.string.isRequired,
  filter: PropTypes.shape({
    placeholder: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["disease", "agent"]).isRequired,
  }).isRequired,
  itemLabels: PropTypes.shape({
    indications: PropTypes.string.isRequired,
    templateId: PropTypes.string.isRequired,
    updatedDate: PropTypes.string.isRequired,
  }).isRequired,
  regimens: PropTypes.arrayOf(PropTypes.object).isRequired,
  noDataText: PropTypes.string.isRequired,
  sharedTelemetryData: PropTypes.shape({
    affordance: PropTypes.string.isRequired,
    pubType: PropTypes.string.isRequired,
  }).isRequired,
};

export default ChemoRegimensFilter;
